/**
 * Validation Helper for Filter Module
 *
 * This file contains all validation and sanitization utilities for the filter system.
 * It focuses on input validation, XSS prevention, and data sanitization.
 *
 * Responsibilities:
 * - Search term validation (XSS detection)
 * - Filter label sanitization
 * - Input validation and cleaning
 * - Security checks for user input
 *
 * @module filter.validation.helper
 */

import { Injectable } from 'framework/di';

import {
  EMAIL_REGEX,
  EVENT_REGEX,
  HEX_COLOR_REGEX,
  RGB_COLOR_REGEX,
  XSS_REGEX,
} from './filter.constants';

/**
 * Validation Helper for Filter Module
 *
 * Provides comprehensive validation and sanitization utilities for the filter system,
 * including XSS detection, input validation, and data sanitization for secure filter operations.
 *
 * This helper can be extended by Technical Support teams to customize validation rules
 * for specific shops, including custom XSS patterns, allowed characters, or sanitization behavior.
 *
 * @remarks
 * Security Approach:
 * - XSS detection uses pattern matching for common attack vectors (script tags, event handlers, JavaScript)
 * - Sanitization blocks malicious content rather than attempting to fix it
 * - All HTML is treated as potentially dangerous unless explicitly validated
 * - DOM events list includes 100+ event handlers for comprehensive XSS prevention
 *
 * Performance Optimizations:
 * - DOM events array is cached as static constant
 * - Regex patterns are compiled once and reused
 * - Validation methods are stateless for parallel execution
 *
 * @example
 * Extend to customize XSS detection:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
 *   return class CustomValidationHelper extends FilterValidationHelper {
 *     isBadSearchTerm(term) {
 *       // Add shop-specific blocked terms
 *       if (typeof term === 'string' && term.includes('shop-blocked-word')) {
 *         return true;
 *       }
 *       return super.isBadSearchTerm(term);
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override to customize allowed characters:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
 *   return class extends FilterValidationHelper {
 *     isAlphanumeric(value, allowedChars) {
 *       // Allow additional characters for Asian market shops
 *       const customChars = allowedChars + '。、';
 *       return super.isAlphanumeric(value, customChars);
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Add custom validation logging:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
 *   return class extends FilterValidationHelper {
 *     sanitizeFilterLabel(label) {
 *       const result = super.sanitizeFilterLabel(label);
 *       if (result === '[Blocked Content]') {
 *         console.warn('Blocked malicious filter label:', label);
 *       }
 *       return result;
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterValidationHelper {
  /**
   * Checks if a search term contains potentially malicious content.
   *
   * Detects XSS patterns, JavaScript code, HTML tags, and event handlers using
   * multi-layered pattern matching for comprehensive security.
   *
   * @remarks
   * Detection Strategy:
   * - JavaScript function calls: alert(), console.log(), execCommand, etc.
   * - HTML tags: <script>, <iframe>, <meta>, and any tag-like patterns
   * - DOM event handlers: onclick, onload, onerror, etc. (100+ events)
   * - XSS vectors: javascript: protocol, data: URIs, HTML entities
   * - Obfuscated patterns: spaced javascript keywords, encoded characters
   *
   * This method is security-critical and uses cached regex patterns for performance.
   * Override to add shop-specific blocked patterns or modify detection sensitivity.
   *
   * @param term - The search term to validate. Non-string values return false.
   * @returns true if the term contains malicious patterns, false if safe or non-string
   *
   * @example
   * Basic validation:
   * ```typescript
   * if (this.validationHelper.isBadSearchTerm(userInput)) {
   *   console.warn('Blocked malicious search term');
   *   return;
   * }
   * ```
   *
   * @example
   * Extend to add shop-specific blocked terms:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
   *   return class extends FilterValidationHelper {
   *     isBadSearchTerm(term) {
   *       // Block shop-specific terms first
   *       if (typeof term === 'string') {
   *         const blockedTerms = ['competitor-brand', 'banned-keyword'];
   *         if (blockedTerms.some(blocked => term.toLowerCase().includes(blocked))) {
   *           return true;
   *         }
   *       }
   *       return super.isBadSearchTerm(term);
   *     }
   *   };
   * });
   * ```
   */
  isBadSearchTerm(term: unknown): boolean {
    if (typeof term !== 'string') return false;

    const normalizedTerm = term.toLowerCase();

    // Use cached event regex for performance
    const isPotentialEvent = EVENT_REGEX.test(normalizedTerm);

    // Detect actual JavaScript function calls
    const isAlert = (normalizedTerm.match(/alert\(/g) || []).length > 0;
    const isConsoleLog = (normalizedTerm.match(/console\.log\(/g) || []).length > 0;
    const isExecCommand = (normalizedTerm.match(/execCommand/g) || []).length > 0;
    const isCookie = (normalizedTerm.match(/document\.cookie/g) || []).length > 0;
    // Detect obfuscated javascript - max 2 chars between each letter to avoid false positives
    const isJavascript =
      (
        normalizedTerm.match(/j.{0,2}a.{0,2}v.{0,2}a.{0,2}s.{0,2}c.{0,2}r.{0,2}i.{0,2}p.{0,2}t/g) ||
        []
      ).length > 0;

    // Detect HTML tags (more specific - must have tag name after < or before >)
    const hasHtmlTags = /<\w+|<\/\w+|\w+>/g.test(normalizedTerm);
    const isMetaTag = (normalizedTerm.match(/<meta/gi) || []).length > 0;

    // XSS-specific patterns (use cached regex)
    const isXssTerm = XSS_REGEX.test(normalizedTerm);

    if (
      hasHtmlTags ||
      isAlert ||
      isConsoleLog ||
      isExecCommand ||
      isCookie ||
      isJavascript ||
      isPotentialEvent ||
      isMetaTag ||
      isXssTerm
    ) {
      return true;
    }

    return false;
  }

  /**
   * Sanitizes a filter label by removing or blocking malicious content.
   *
   * Returns a safe string that can be displayed in the UI. Uses strict blocking
   * approach - malicious content is replaced with placeholder rather than sanitized.
   *
   * @remarks
   * Security Strategy:
   * - Invalid/non-string inputs return '[Invalid Label]'
   * - Malicious content (detected by isBadSearchTerm) returns '[Blocked Content]'
   * - Safe content is returned as-is (browser's textContent/setAttribute handles safely)
   * - No attempt to "fix" malicious content - blocking is safer than sanitization
   *
   * Override this method to customize sanitization behavior, blocked content messages,
   * or add shop-specific label transformations.
   *
   * @param label - The filter label to sanitize. Non-string values are considered invalid.
   * @returns Sanitized label string, '[Invalid Label]' for non-strings, or
   * '[Blocked Content]' for malicious input
   *
   * @example
   * Basic usage:
   * ```typescript
   * const safeLabel = this.validationHelper.sanitizeFilterLabel(userLabel);
   * element.textContent = safeLabel; // Safe to display
   * ```
   *
   * @example
   * Extend to customize blocked messages:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
   *   return class extends FilterValidationHelper {
   *     sanitizeFilterLabel(label) {
   *       if (!label || typeof label !== 'string') {
   *         return this.getLocalizedMessage('invalid');
   *       }
   *       if (this.isBadSearchTerm(label)) {
   *         console.warn('Blocked malicious label:', label);
   *         return this.getLocalizedMessage('blocked');
   *       }
   *       return label;
   *     }
   *   };
   * });
   * ```
   */
  sanitizeFilterLabel(label: unknown): string {
    if (!label || typeof label !== 'string') {
      return '[Invalid Label]';
    }

    // Check for malicious content (XSS patterns, JavaScript code, etc.)
    if (this.isBadSearchTerm(label)) {
      // BLOCK malicious content - don't display JavaScript code even as text
      return '[Blocked Content]';
    }

    // Safe content - return as-is (textContent/setAttribute handle safely)
    return label;
  }

  /**
   * Validates if a string is a valid URL.
   *
   * Uses the URL constructor for validation, which checks protocol, host, and format.
   *
   * @param url - The URL string to validate. Should include protocol (http://, https://).
   * @returns true if the string is a valid, parseable URL, false otherwise
   *
   * @example
   * Validate user-provided URL:
   * ```typescript
   * if (this.validationHelper.isValidUrl(imageUrl)) {
   *   this.loadImage(imageUrl);
   * }
   * ```
   */
  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validates if a string contains only alphanumeric characters and allowed special chars.
   *
   * Useful for validating filter keys, handles, product IDs, and other identifiers.
   * Override to support additional character sets for international shops.
   *
   * @param value - The value to validate. Must contain only a-z, A-Z, 0-9, and allowed special chars.
   * @param allowedChars - Additional characters to allow beyond alphanumeric. Default: '-_'
   * @returns true if the value matches the pattern, false otherwise
   *
   * @example
   * Validate product handle:
   * ```typescript
   * if (this.validationHelper.isAlphanumeric(handle)) {
   *   this.fetchProduct(handle);
   * }
   * ```
   *
   * @example
   * Allow custom characters:
   * ```typescript
   * const isValid = this.validationHelper.isAlphanumeric(value, '-_./@');
   * ```
   *
   * @example
   * Extend for international character support:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterValidationHelper', (FilterValidationHelper) => {
   *   return class extends FilterValidationHelper {
   *     isAlphanumeric(value, allowedChars = '-_') {
   *       // Support Chinese characters for Asian market
   *       const pattern = new RegExp(`^[a-zA-Z0-9${allowedChars}\u4e00-\u9fa5]+$`);
   *       return pattern.test(value);
   *     }
   *   };
   * });
   * ```
   */
  isAlphanumeric(value: string, allowedChars: string = '-_'): boolean {
    const pattern = new RegExp(`^[a-zA-Z0-9${allowedChars}]+$`);
    return pattern.test(value);
  }

  /**
   * Validates if a string is a valid email address.
   *
   * Uses basic email format validation (local@domain.tld).
   *
   * @param email - The email address to validate. Should follow standard email format.
   * @returns true if the email matches basic email pattern, false otherwise
   *
   * @example
   * Validate customer email:
   * ```typescript
   * if (this.validationHelper.isValidEmail(customerEmail)) {
   *   this.sendNotification(customerEmail);
   * }
   * ```
   */
  isValidEmail(email: string): boolean {
    return EMAIL_REGEX.test(email);
  }

  /**
   * Validates if a value is a valid number.
   *
   * Checks both number types and numeric strings, rejecting NaN and Infinity.
   *
   * @param value - The value to validate. Can be a number or numeric string.
   * @returns true if the value is a valid, finite number, false otherwise
   *
   * @example
   * Validate price input:
   * ```typescript
   * if (this.validationHelper.isValidNumber(userPrice)) {
   *   this.applyDiscount(Number(userPrice));
   * }
   * ```
   */
  isValidNumber(value: unknown): boolean {
    if (typeof value === 'number') {
      return !Number.isNaN(value) && Number.isFinite(value);
    }
    if (typeof value === 'string') {
      const num = Number(value);
      return !Number.isNaN(num) && Number.isFinite(num);
    }
    return false;
  }

  /**
   * Validates if a value is within a specified range.
   *
   * Performs inclusive range checking (min <= value <= max).
   *
   * @param value - The numeric value to validate
   * @param min - Minimum allowed value (inclusive)
   * @param max - Maximum allowed value (inclusive)
   * @returns true if the value is within range [min, max], false otherwise
   *
   * @example
   * Validate quantity input:
   * ```typescript
   * if (this.validationHelper.isInRange(quantity, 1, 100)) {
   *   this.addToCart(product, quantity);
   * }
   * ```
   */
  isInRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max;
  }

  /**
   * Sanitizes a string by removing all HTML tags.
   *
   * Strips all HTML markup, leaving only text content. Useful for displaying
   * user-generated content safely.
   *
   * @param html - The HTML string to sanitize. Can contain any HTML markup.
   * @returns Plain text without HTML tags. Empty string if input is empty.
   *
   * @example
   * Strip HTML from user input:
   * ```typescript
   * const plainText = this.validationHelper.stripHtml(userInput);
   * element.textContent = plainText;
   * ```
   */
  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }

  /**
   * Escapes special characters in a string for safe use in HTML.
   *
   * Converts characters like <, >, &, ", ' to their HTML entity equivalents.
   *
   * @param text - The text to escape. Plain text that may contain special characters.
   * @returns Escaped text safe for HTML insertion via innerHTML
   *
   * @example
   * Safely insert user text into HTML:
   * ```typescript
   * const escaped = this.validationHelper.escapeHtml(userText);
   * element.innerHTML = `<div>${escaped}</div>`;
   * ```
   */
  escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Validates if a string is empty or only contains whitespace.
   *
   * Handles multiple types: null/undefined, strings, arrays, and objects.
   * Strings are considered empty if they contain only whitespace.
   *
   * @param value - The value to check. Can be any type.
   * @returns true if the value is empty, whitespace-only, null, undefined,
   * an empty array, or an empty object, false otherwise
   *
   * @example
   * Validate required fields:
   * ```typescript
   * if (this.validationHelper.isEmpty(searchQuery)) {
   *   this.showError('Search query is required');
   *   return;
   * }
   * ```
   */
  isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  /**
   * Validates if a value is a valid JSON string.
   *
   * Attempts to parse the string as JSON to verify validity.
   *
   * @param value - The string to validate. Must be valid JSON format.
   * @returns true if the string can be parsed as JSON, false otherwise
   *
   * @example
   * Validate JSON configuration:
   * ```typescript
   * if (this.validationHelper.isValidJson(configString)) {
   *   const config = JSON.parse(configString);
   *   this.applyConfiguration(config);
   * }
   * ```
   */
  isValidJson(value: string): boolean {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validates if a string matches a specific pattern.
   *
   * Uses custom regex pattern for flexible validation rules.
   *
   * @param value - The value to validate against the pattern
   * @param pattern - The regex pattern to match against
   * @returns true if the value matches the pattern, false otherwise
   *
   * @example
   * Validate custom format:
   * ```typescript
   * const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
   * if (this.validationHelper.matchesPattern(phone, phonePattern)) {
   *   this.savePhoneNumber(phone);
   * }
   * ```
   */
  matchesPattern(value: string, pattern: RegExp): boolean {
    return pattern.test(value);
  }

  /**
   * Sanitizes a value for use as a URL parameter.
   *
   * Removes or encodes potentially problematic characters using encodeURIComponent.
   *
   * @param value - The value to sanitize. Can contain any characters.
   * @returns Sanitized value safe for URL parameters, with special characters encoded
   *
   * @example
   * Sanitize filter value for URL:
   * ```typescript
   * const safeValue = this.validationHelper.sanitizeUrlParam(filterValue);
   * const url = `/products?brand=${safeValue}`;
   * ```
   */
  sanitizeUrlParam(value: string): string {
    // Remove any characters that could break URL structure
    return encodeURIComponent(value.trim());
  }

  /**
   * Validates if a string length is within acceptable bounds.
   *
   * Checks if string length falls between minimum and maximum values (inclusive).
   *
   * @param value - The string to validate
   * @param minLength - Minimum allowed length (inclusive). Default: 0
   * @param maxLength - Maximum allowed length (inclusive). Default: Infinity
   * @returns true if the length is within bounds [minLength, maxLength], false otherwise
   *
   * @example
   * Validate search term length:
   * ```typescript
   * if (!this.validationHelper.isValidLength(searchTerm, 2, 100)) {
   *   this.showError('Search term must be 2-100 characters');
   *   return;
   * }
   * ```
   */
  isValidLength(value: string, minLength: number = 0, maxLength: number = Infinity): boolean {
    const length = value.length;
    return length >= minLength && length <= maxLength;
  }

  /**
   * Validates if an array contains only unique values.
   *
   * Uses Set comparison to detect duplicates efficiently.
   *
   * @param array - The array to validate. Can contain any types.
   * @returns true if all values are unique (no duplicates), false otherwise
   *
   * @example
   * Validate filter selection:
   * ```typescript
   * if (!this.validationHelper.hasUniqueValues(selectedFilters)) {
   *   this.removeDuplicates(selectedFilters);
   * }
   * ```
   */
  hasUniqueValues(array: unknown[]): boolean {
    return new Set(array).size === array.length;
  }

  /**
   * Validates if a value is a valid integer.
   *
   * Accepts both number types and numeric strings. Rejects decimals.
   *
   * @param value - The value to validate. Can be a number or numeric string.
   * @returns true if the value is a whole number (integer), false otherwise
   *
   * @example
   * Validate quantity:
   * ```typescript
   * if (this.validationHelper.isInteger(quantity)) {
   *   this.updateQuantity(Number(quantity));
   * }
   * ```
   */
  isInteger(value: unknown): boolean {
    if (typeof value === 'number') {
      return Number.isInteger(value);
    }
    if (typeof value === 'string') {
      const num = Number(value);
      return Number.isInteger(num);
    }
    return false;
  }

  /**
   * Validates if a value is a valid positive number.
   *
   * Checks if the value is a valid number and greater than zero.
   *
   * @param value - The value to validate. Can be a number or numeric string.
   * @returns true if the value is a positive number (> 0), false otherwise
   *
   * @example
   * Validate price:
   * ```typescript
   * if (this.validationHelper.isPositiveNumber(price)) {
   *   this.calculateTotal(Number(price));
   * }
   * ```
   */
  isPositiveNumber(value: unknown): boolean {
    if (!this.isValidNumber(value)) return false;
    const num = typeof value === 'number' ? value : Number(value);
    return num > 0;
  }

  /**
   * Trims and normalizes whitespace in a string.
   *
   * Removes leading/trailing whitespace and replaces multiple spaces with single space.
   *
   * @param value - The string to normalize. Can contain any amount of whitespace.
   * @returns Normalized string with single spaces and no leading/trailing whitespace
   *
   * @example
   * Normalize user input:
   * ```typescript
   * const cleaned = this.validationHelper.normalizeWhitespace(userInput);
   * // 'hello    world  ' becomes 'hello world'
   * ```
   */
  normalizeWhitespace(value: string): string {
    return value.trim().replace(/\s+/g, ' ');
  }

  /**
   * Validates if a color string is valid (hex, rgb, rgba, or named color).
   *
   * Supports multiple color formats:
   * - Hex: #fff, #ffffff, #ffffffff
   * - RGB/RGBA: rgb(255, 0, 0), rgba(255, 0, 0, 0.5)
   * - Named colors: red, blue, transparent, etc.
   *
   * @param color - The color string to validate in any CSS color format.
   * @returns true if the color is valid in any supported format, false otherwise
   *
   * @example
   * Validate color before applying:
   * ```typescript
   * if (this.validationHelper.isValidColor(userColor)) {
   *   element.style.backgroundColor = userColor;
   * }
   * ```
   */
  isValidColor(color: string): boolean {
    // Use cached regex for hex colors
    if (HEX_COLOR_REGEX.test(color)) return true;

    // Use cached regex for RGB/RGBA
    if (RGB_COLOR_REGEX.test(color)) return true;

    // Named colors (basic check - create temp element)
    const tempElement = document.createElement('div');
    tempElement.style.color = color;
    return tempElement.style.color !== '';
  }

  /**
   * Converts a string to a URL-friendly slug.
   *
   * Transforms text into a format suitable for URLs by:
   * - Converting to lowercase
   * - Replacing spaces with hyphens
   * - Removing special characters
   * - Collapsing multiple hyphens
   *
   * @param text - The text to slugify. Can contain any characters.
   * @returns URL-friendly slug string with only lowercase letters, numbers, and hyphens
   *
   * @example
   * Create URL slug from filter name:
   * ```typescript
   * const slug = this.validationHelper.slugify('Brand Name & Color');
   * // Returns: 'brand-name-color'
   * const url = `/filters/${slug}`;
   * ```
   */
  slugify(text: string): string {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/[^\w\-]+/g, '') // Remove non-word characters except hyphens
      .replace(/\-\-+/g, '-'); // Replace multiple hyphens with single hyphen
  }
}
